import React, { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { NumericKeyboard } from "../components/common/NumericKeyboard";
import { Loader2, Check, ShoppingCart, Tv, Zap, Wifi, ArrowLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, cn } from "../lib/utils";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const billTypes = [
  { id: "amazon", name: "Amazon", icon: ShoppingCart, color: "bg-[#FF9900]", light: "bg-[#FF9900]/10", text: "text-[#FF9900]" },
  { id: "netflix", name: "Netflix", icon: Tv, color: "bg-[#E50914]", light: "bg-[#E50914]/10", text: "text-[#E50914]" },
  { id: "electricity", name: "Electricity", icon: Zap, color: "bg-[#F59E0B]", light: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" },
  { id: "internet", name: "Internet", icon: Wifi, color: "bg-[#3B82F6]", light: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" },
];

export const BillPayment = () => {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedBill, setSelectedBill] = useState(null);
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSelectBill = (bill) => {
    setSelectedBill(bill);
    setStep(2);
  };

  const handleAmountSubmit = () => {
    const amountValue = parseFloat(amount);
    if (!amountValue || amountValue <= 0) return toast.error("Please enter a valid amount");
    if (amountValue > user.available_balance) return toast.error("Insufficient balance");
    setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/bill-payment`, {
        bill_type: selectedBill.name,
        amount: parseFloat(amount),
        transaction_pin: pin,
      });
      setResult(response.data);
      await refreshUser();
      console.log(response.data)
      toast.success("Payment Successful");
    } catch (err) {
      toast.error(err.response?.data?.message || "Payment failed");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedBill(null);
    setAmount("");
    setPin("");
    setResult(null);
  };

  if (result) {
    return (
      <div className="max-w-md mx-auto animate-in zoom-in-95 duration-500">
        <Card className="border-none shadow-2xl overflow-hidden rounded-[2.5rem]">
          <div className="bg-primary p-10 text-white text-center relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16" />
            <div className="w-20 h-20 rounded-3xl bg-white/20 backdrop-blur-md flex items-center justify-center mx-auto mb-6 border border-white/20 shadow-xl">
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h2 className="text-2xl font-black tracking-tight mb-2">Transaction Successful</h2>
            <p className="text-white/70 text-sm font-bold uppercase tracking-widest">Receipt Generated</p>
          </div>
          <CardContent className="p-8 space-y-6">
            <div className="text-center py-6 bg-slate-50 rounded-3xl border border-slate-100">
              <p className="text-[12px] font-black uppercase text-slate-400 tracking-widest mb-1">Total Paid</p>
              <p className="text-4xl font-black text-slate-900 leading-none">
                {formatCurrency(result.amount, user?.currency)}
              </p>
            </div>
            <div className="space-y-4 pt-2 px-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-400 uppercase tracking-tighter">Service</span>
                <span className="text-sm font-bold text-slate-900">{selectedBill?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-slate-400 uppercase tracking-tighter">Reference</span>
                <span className="text-[11px] font-mono bg-slate-100 px-2 py-1 rounded text-slate-600">
                  #{result.id.slice(0, 10).toUpperCase()}
                </span>
              </div>
            </div>
            <Button onClick={reset} className="w-full h-14 rounded-2xl font-bold text-md mt-4 shadow-lg shadow-primary/20">
              Return to Payments
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Dynamic Header */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-4">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} className="p-2 bg-white rounded-xl shadow-sm border border-slate-100 hover:bg-slate-50 transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <div>
            <h1 className="text-lg font-black tracking-tight text-slate-900 leading-tight">Bill Payment</h1>
            <p className="text-[12px] font-bold text-primary uppercase tracking-[0.2em] mt-0.5">Step {step} of 3</p>
          </div>
        </div>
      </div>

      {step === 1 && (
        <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-right-4 duration-300">
          {billTypes.map((bill) => (
            <button
              key={bill.id}
              onClick={() => handleSelectBill(bill)}
              className="group relative flex flex-col items-center gap-4 p-8 bg-white rounded-[2rem] border border-slate-100 hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
            >
              <div className={cn("w-16 h-16 rounded-[1.5rem] flex items-center justify-center transition-transform group-hover:scale-110 duration-300", bill.light)}>
                <bill.icon className={cn("w-8 h-8", bill.text)} />
              </div>
              <span className="font-bold text-slate-900 text-sm tracking-tight">{bill.name}</span>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <Card className="border-none shadow-none bg-transparent animate-in slide-in-from-right-4 duration-300">
          <CardContent className="p-0 space-y-6">
            <div className="bg-slate-900 p-6 rounded-[2rem] text-white shadow-2xl relative overflow-hidden">
               <div className="absolute bottom-0 right-0 w-24 h-24 bg-primary/20 rounded-full blur-3xl -mb-12 -mr-12" />
               <p className="text-[12px] font-bold uppercase tracking-widest text-white/50 mb-1">Available Funds</p>
               <p className="text-xl font-black">{formatCurrency(user?.available_balance || 0, user?.currency)}</p>
            </div>
            
            <div className="space-y-4 p-2">
              <div className="space-y-2">
                <Label className="text-[11px] font-black uppercase text-slate-400 tracking-wider ml-1">Payment Amount</Label>
                <div className="relative">
                    <Input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="h-16 text-2xl font-black rounded-2xl bg-white border-slate-100 focus:border-primary transition-all pr-12"
                        placeholder="0.00"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 font-bold">
                        {user?.currency}
                    </div>
                </div>
              </div>
              <Button onClick={handleAmountSubmit} className="w-full h-14 rounded-2xl font-bold text-sm shadow-lg shadow-primary/20 mt-4">
                Review Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <div className="animate-in slide-in-from-right-4 duration-300 space-y-6">
            <div className="text-center space-y-2">
                <h3 className="text-xl font-black text-slate-900">Authorize Payment</h3>
                <p className="text-sm font-medium text-slate-500">Confirm payment of {formatCurrency(amount, user?.currency)} for {selectedBill?.name}</p>
            </div>
            
            <div className="flex justify-center gap-3 py-4">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={cn(
                            "w-14 h-14 rounded-2xl border-2 flex items-center justify-center text-2xl transition-all duration-300",
                            pin.length > i ? "border-primary bg-primary/5 text-primary scale-105" : "border-slate-100 bg-slate-50"
                        )}
                    >
                        {pin.length > i ? "•" : ""}
                    </div>
                ))}
            </div>

            <NumericKeyboard value={pin} onChange={setPin} maxLength={4} />

            <Button
                onClick={handleSubmit}
                className="w-full h-14 rounded-2xl font-bold text-md shadow-lg shadow-primary/20"
                disabled={loading || pin.length !== 4}
            >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : `Confirm & Pay`}
            </Button>
        </div>
      )}
    </div>
  );
};